import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowUpRight, Lock, Plus } from 'lucide-react';
import {
  HELP_BODY_MAX,
  HELP_STATUSES,
  HELP_STATUS_ADMIN_LABELS,
  HELP_STATUS_LABELS,
  HELP_TYPE_LABELS,
  type HelpStatus,
} from '@ready/shared';
import { useApi, type HelpRequestDetail } from '@/lib/api';
import { formatDateTime } from '@/lib/dates';
import { Badge, Button, Card, ErrorText, PageHeader, Select, Textarea } from '@/components/ui';
import { HelpDialog } from '@/components/help/help-dialog';
import { AttachmentList, AttachmentPicker, useAttachments } from '@/components/help/attachments';

/*
 * Help requests: the requester's list (Support Requests) and one request's
 * conversation, shared by the requester and the inbox's admins (who also see
 * internal notes, assign, change status, and, for orgs, move it to Nirvik).
 */

const STATUS_STYLE: Record<HelpStatus, string> = {
  open: 'border-primary/40 bg-primary/10 text-primary-strong',
  in_progress: 'border-chakra-navy/40 bg-chakra-navy/10 text-chakra-navy',
  waiting: 'border-saffron/40 bg-saffron/10 text-saffron',
  resolved: 'border-india-green/40 bg-india-green/10 text-india-green',
  closed: 'border-border bg-muted text-muted-foreground',
};

export function StatusPill({ status, admin = false }: { status: HelpStatus; admin?: boolean }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[status]}`}
    >
      {(admin ? HELP_STATUS_ADMIN_LABELS : HELP_STATUS_LABELS)[status]}
    </span>
  );
}

export function MyHelpPage() {
  const api = useApi();
  const [asking, setAsking] = useState(false);
  const list = useQuery({ queryKey: ['myHelp'], queryFn: api.myHelpRequests });
  return (
    <div>
      <PageHeader
        title="Support Requests"
        description="Questions and requests you sent to the Nirvik Ready team or your organizations."
        action={
          <Button className="w-full sm:w-auto" onClick={() => setAsking(true)}>
            <Plus className="size-4" aria-hidden /> New request
          </Button>
        }
      />
      <ErrorText error={list.error} />
      <Card>
        {list.isPending ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : list.data?.requests.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No requests yet. Ask a question or request something any time.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {list.data?.requests.map((r) => (
              <li key={r.id}>
                <Link
                  to={r.id}
                  className="flex items-start justify-between gap-3 py-3 hover:text-primary"
                >
                  <span className="min-w-0">
                    <span
                      className={`block truncate ${r.unread ? 'font-semibold' : 'font-medium'}`}
                    >
                      {r.unread && (
                        <span
                          className="mr-1.5 inline-block size-2 rounded-full bg-primary"
                          aria-label="New reply"
                        />
                      )}
                      {r.subject}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {r.inboxName} · {HELP_TYPE_LABELS[r.type]} · Updated{' '}
                      {formatDateTime(r.updatedAt)}
                    </span>
                  </span>
                  <StatusPill status={r.status} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
      {asking && <HelpDialog onClose={() => setAsking(false)} />}
    </div>
  );
}

export function HelpRequestPage() {
  const { requestId = '' } = useParams();
  const api = useApi();
  const detail = useQuery({
    queryKey: ['help', requestId],
    queryFn: () => api.helpRequest(requestId),
  });
  if (detail.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (detail.isError) return <ErrorText error={detail.error} />;
  return <Conversation key={detail.data.id} request={detail.data} />;
}

function Conversation({ request: r }: { request: HelpRequestDetail }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const staff = r.access === 'manage';
  const adminView = r.access !== 'requester';
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['help', r.id] });
    queryClient.invalidateQueries({ queryKey: ['helpInbox'] });
    queryClient.invalidateQueries({ queryKey: ['myHelp'] });
    queryClient.invalidateQueries({ queryKey: ['inboxCounts'] });
  };
  const update = useMutation({
    mutationFn: (input: { status?: HelpStatus; assigneeId?: string | null }) =>
      api.updateHelp(r.id, input),
    onSuccess: refresh,
  });
  const move = useMutation({ mutationFn: () => api.moveHelpToPlatform(r.id), onSuccess: refresh });
  const back = adminView
    ? r.orgId
      ? { to: `/organizations/${r.orgId}/help`, label: 'Helpdesk' }
      : { to: '/admin/help', label: 'Helpdesk' }
    : { to: '/help', label: 'Support Requests' };

  return (
    <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_18rem]">
      <div className="min-w-0 space-y-4">
        <PageHeader title={r.subject} back={back} />
        <div className="-mt-4 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <StatusPill status={r.status} admin={adminView} />
          <span>
            To {r.inboxName} · {HELP_TYPE_LABELS[r.type]} · {formatDateTime(r.createdAt)}
          </span>
        </div>
        <ol className="space-y-3">
          {r.messages.map((m) => (
            <li
              key={m.id}
              className={`rounded-xl border p-4 text-sm ${m.internal ? 'border-saffron/40 bg-saffron/5' : m.fromStaff ? 'border-primary/30 bg-primary/5' : 'border-border bg-card'}`}
            >
              <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{m.author.name}</span>
                {m.internal && (
                  <span className="inline-flex items-center gap-1 text-saffron">
                    <Lock className="size-3" aria-hidden /> Internal note
                  </span>
                )}
                {m.fromStaff && !m.internal && <Badge>{r.inboxName}</Badge>}
                <span>{formatDateTime(m.createdAt)}</span>
              </div>
              <p className="whitespace-pre-wrap break-words">{m.body}</p>
              <AttachmentList files={m.files} />
            </li>
          ))}
        </ol>
        {r.status === 'closed' && !staff ? (
          <Card>
            <p className="text-sm text-muted-foreground">
              This request is closed. Need more help? Send a new request from the sidebar.
            </p>
          </Card>
        ) : (
          (staff || r.isRequester) && <ReplyBox request={r} staff={staff} onSent={refresh} />
        )}
      </div>

      <aside className="space-y-4">
        <Card title="Details">
          <dl className="space-y-2 text-sm">
            <div>
              <dt className="text-muted-foreground">From</dt>
              <dd className="break-all">{r.requester.name ?? r.requester.email}</dd>
              {r.requester.name && (
                <dd className="break-all text-xs text-muted-foreground">{r.requester.email}</dd>
              )}
            </div>
            {r.pageUrl && (
              <div>
                <dt className="text-muted-foreground">Sent from</dt>
                <dd>
                  <Link to={r.pageUrl} className="font-mono text-xs hover:text-primary">
                    {r.pageUrl}
                  </Link>
                </dd>
              </div>
            )}
            {r.movedFromOrgId && (
              <div className="text-xs text-muted-foreground">
                Passed to the Nirvik Ready team by the organization.
              </div>
            )}
          </dl>
        </Card>
        {staff && (
          <Card title="Manage">
            <div className="space-y-3">
              <label className="block text-sm">
                <span className="mb-1 block font-medium">Status</span>
                <Select
                  value={r.status}
                  disabled={update.isPending}
                  onChange={(e) => update.mutate({ status: e.target.value as HelpStatus })}
                >
                  {HELP_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {HELP_STATUS_ADMIN_LABELS[s]}
                    </option>
                  ))}
                </Select>
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium">Assigned to</span>
                <Select
                  value={r.assigneeId ?? ''}
                  disabled={update.isPending}
                  onChange={(e) => update.mutate({ assigneeId: e.target.value || null })}
                >
                  <option value="">Nobody</option>
                  {r.assignees.map((a) => (
                    <option key={a.userId} value={a.userId}>
                      {a.name}
                    </option>
                  ))}
                </Select>
              </label>
              {r.orgId && (
                <Button
                  variant="secondary"
                  className="w-full"
                  disabled={move.isPending}
                  onClick={() => {
                    if (
                      window.confirm(
                        'Pass this request to the Nirvik Ready team? It leaves your inbox, and the requester is told in the conversation.',
                      )
                    )
                      move.mutate();
                  }}
                >
                  <ArrowUpRight className="size-4" aria-hidden /> Move to Nirvik Ready
                </Button>
              )}
              <ErrorText error={update.error ?? move.error} />
            </div>
          </Card>
        )}
        {!staff && r.isRequester && r.status !== 'closed' && (
          <Card>
            {r.status === 'resolved' ? (
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => update.mutate({ status: 'open' })}
              >
                Reopen
              </Button>
            ) : (
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => update.mutate({ status: 'resolved' })}
              >
                Mark resolved
              </Button>
            )}
            <ErrorText error={update.error} />
          </Card>
        )}
        {r.access === 'read' && (
          <p className="text-xs text-muted-foreground">
            You&rsquo;re viewing as an admin of a parent organization.
          </p>
        )}
      </aside>
    </div>
  );
}

function ReplyBox({
  request: r,
  staff,
  onSent,
}: {
  request: HelpRequestDetail;
  staff: boolean;
  onSent: () => void;
}) {
  const api = useApi();
  const [body, setBody] = useState('');
  const [internal, setInternal] = useState(false);
  const attachments = useAttachments();
  const send = useMutation({
    mutationFn: () =>
      api.replyToHelp(r.id, { body: body.trim(), internal, fileIds: attachments.ids }),
    onSuccess: () => {
      setBody('');
      setInternal(false);
      attachments.clear();
      onSent();
    },
  });
  return (
    <Card>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (body.trim() && !attachments.busy) send.mutate();
        }}
      >
        <Textarea
          aria-label="Your reply"
          rows={4}
          maxLength={HELP_BODY_MAX}
          value={body}
          placeholder={
            internal
              ? 'A note only admins of this inbox see'
              : staff
                ? `Reply to ${r.requester.name ?? r.requester.email}`
                : 'Add details or reply'
          }
          onChange={(e) => setBody(e.target.value)}
        />
        <AttachmentPicker state={attachments} />
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          {staff ? (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="size-5 accent-primary"
                checked={internal}
                onChange={(e) => setInternal(e.target.checked)}
              />
              Internal note (admins only)
            </label>
          ) : (
            <span />
          )}
          <Button
            type="submit"
            className="w-full sm:w-auto"
            disabled={!body.trim() || attachments.busy || send.isPending}
          >
            {internal ? 'Add note' : 'Send reply'}
          </Button>
        </div>
        <ErrorText error={send.error} />
      </form>
    </Card>
  );
}
